import {ClassComponent, Vnode} from 'mithril';
import app from 'flarum/common/app';
import {ApiPayloadSingle} from 'flarum/common/Store';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';
import classList from 'flarum/common/utils/classList';
import stringToColor from 'flarum/common/utils/stringToColor';
import humanTime from 'flarum/common/helpers/humanTime';
import icon from 'flarum/common/helpers/icon';
import Embed, {Fallback, OpenGraph} from '../models/Embed';
import formatDuration from '../utils/formatDuration';
import formatSize from '../utils/formatSize';
import imageUrl from '../utils/imageUrl';
import Icon from './Icon';

interface BlockEmbedAttrs {
    style?: string
    withoutOpengraph?: boolean
    embed: Embed
    canDisable: boolean
    disable: () => Promise<any>
}

export default class BlockEmbed implements ClassComponent<BlockEmbedAttrs> {
    refreshing: boolean = false;
    disabling: boolean = false;
    expandedDescription: boolean = false;
    youtubePlayer: boolean = false;

    view(vnode: Vnode<BlockEmbedAttrs, this>) {
        const {embed} = vnode.attrs;

        // If the embed failed to render, we still need a way to trigger a refresh
        if (!embed.hasContent()) {
            return m('.rich-embed-block', [
                this.controls(vnode, embed),
                m('.rich-embed-site-name', embed.domain()),
                m('.rich-embed-title', embed.id()),
                m('.rich-embed-description', [
                    embed.state() === 'retrieving' ? [
                        LoadingIndicator.component(),
                        ' ',
                        app.translator.trans('kilowhat-rich-embeds.lib.embed.missing.retrieving'),
                    ] : app.translator.trans('kilowhat-rich-embeds.lib.embed.missing.other'),
                ]),
            ]);
        }

        const opengraph = embed.opengraph() || {};
        const fallback = embed.fallback() || {};
        const preview = embed.preview() || {};

        let youtubeVideoId: string | undefined;
        let youtubeVideoTimestamp: number = 0;

        if (app.forum.attribute('richEmbedsYoutubePlayer')) {
            let matches = /youtube\.com\/(?:watch.*?v=|shorts\/|v\/|live\/|attribution_link.*?v%3D)([-\w]+)(?:[?|&]t=([0-9]+))?/.exec(embed.id()!);

            if (!matches) {
                matches = /youtu.be\/([-\w]+)(?:\?t=([0-9]+))?/.exec(embed.id()!);
            }

            if (matches) {
                youtubeVideoId = matches[1];

                if (matches.length > 2) {
                    youtubeVideoTimestamp = parseInt(matches[2]);
                }
            }
        }

        // Force vertical layout when video player is enabled so the thumbnail and players are in the same place
        const isHorizontal = vnode.attrs.style === 'horizontal' && !youtubeVideoId;
        const imageContent = youtubeVideoId ? null : this.images(opengraph, vnode.attrs.withoutOpengraph ? fallback : {}, embed);

        return m('.rich-embed-block', {
            className: classList({
                'rich-embed-horizontal': isHorizontal,
                'rich-embed-youtube-supported': youtubeVideoId,
            }),
        }, [
            this.controls(vnode, embed),
            isHorizontal ? imageContent : null,
            m('.rich-embed-site-name', [
                m(Icon, {
                    embed,
                }),
                ' ',
                preview.siteName,
                //this.siteName(embed),
                preview.contentType ? m('span.rich-embed-content-type', preview.contentType) : null,
            ]),
            preview.title ? m('.rich-embed-title', preview.title) : null,
            this.author(embed),
            this.description(preview.description),
            isHorizontal ? null : imageContent,
            this.youtubeIframe(youtubeVideoId, youtubeVideoTimestamp, embed),
            this.stats(embed),
        ]);
    }

    controls(vnode: Vnode<BlockEmbedAttrs, this>, embed: Embed) {
        return m('.rich-embed-controls', [
            embed.canRefresh() ? Tooltip.component({
                text: extractText(app.translator.trans('kilowhat-rich-embeds.lib.controls.refresh')),
            }, Button.component({
                className: 'Button Button--icon',
                icon: this.refreshing ? 'fas fa-spinner fa-pulse' : 'fas fa-sync',
                onclick: (event: Event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    this.refreshing = true;

                    app.request<ApiPayloadSingle>({
                        method: 'POST',
                        url: app.forum.attribute('apiUrl') + '/kilowhat-rich-embeds/get',
                        body: {
                            url: embed.id(),
                            refresh: true,
                        },
                    }).then(response => {
                        app.store.pushPayload(response);

                        this.refreshing = false;

                        m.redraw();
                    }).catch(error => {
                        this.refreshing = false;
                        m.redraw();

                        throw error;
                    });
                },
            })) : null,
            vnode.attrs.canDisable ? Tooltip.component({
                text: extractText(app.translator.trans('kilowhat-rich-embeds.lib.controls.disable')),
            }, Button.component({
                className: 'Button Button--icon',
                icon: 'fas fa-times',
                onclick: (event: Event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (!confirm(extractText(app.translator.trans('kilowhat-rich-embeds.lib.controls.disableConfirmation')))) {
                        return;
                    }

                    this.disabling = true;

                    vnode.attrs.disable().then(() => {
                        this.disabling = false;
                        // Not redrawing here, we expect the main code to do it
                        // This will not always be needed since when the post editor is open the promise will return instantly
                    });
                },
            })) : null,
        ]);
    }

    author(embed: Embed) {
        const preview = embed.preview() || {};

        if (!preview.author || !preview.author.username) {
            return null;
        }

        const {avatarUrl, username, url} = preview.author;

        let avatar;

        if (avatarUrl) {
            avatar = m('img.Avatar.rich-embed-author-avatar', {
                src: imageUrl(avatarUrl),
                alt: app.translator.trans('kilowhat-rich-embeds.lib.embed.altAvatar'),
            });
        } else {
            // Use same method as Flarum to create default avatars
            // Since most external platforms will provide avatar URLs all of the time this will mostly apply to Flarum embeds
            avatar = m('span.Avatar.rich-embed-author-avatar', {
                style: {
                    '--avatar-bg': '#' + stringToColor(username),
                },
            }, username.charAt(0).toUpperCase());
        }

        return m('.rich-embed-author', [
            avatar,
            ' ',
            m((url ? 'a' : 'span') + '.rich-embed-author-username', url ? {href: url} : {}, username),
        ]);
    }

    description(description: string | undefined) {
        if (!description) {
            return null;
        }

        // Based on https://stackoverflow.com/a/33379772
        const trimmable = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u180E\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u2028\u2029\u3000\uFEFF';
        const reg = new RegExp('(?=[' + trimmable + '])');
        const words = description.split(reg);
        let count = 0;
        const excerpt = words.filter((word: string) => {
            count += word.length;
            return count <= 300;
        }).join('');

        const text = this.expandedDescription ? description : excerpt;
        const button = description.length > excerpt.length ? m('button.Button.rich-embed-ellipsis-control', {
            onclick: (event: Event) => {
                event.preventDefault();
                event.stopPropagation();

                this.expandedDescription = !this.expandedDescription;
            },
            title: app.translator.trans('kilowhat-rich-embeds.lib.controls.expandDescription'),
        }, icon('fas fa-ellipsis-h')) : null;

        return m('.rich-embed-description', [
            text,
            ' ',
            button,
        ]);
    }

    images(opengraph: OpenGraph, fallback: Fallback, embed: Embed) {
        const preview = embed.preview() || {};
        const images = preview.images || [];

        if (images.length === 0) {
            return null;
        }

        return m('.rich-embed-images', {
            className: classList({
                'rich-embed-single-image': images.length === 1,
            }),
        }, images.map(image => m('img', {
            src: imageUrl(image.src),
            alt: image.alt || extractText(app.translator.trans('kilowhat-rich-embeds.lib.embed.altImageFallback', {
                website: opengraph.site_name || embed.domain(),
            })),
        })));
    }

    youtubeIframe(videoId?: string, timestamp: number = 0, embed: Embed) {
        if (!this.youtubePlayer) {
            let duration;

            const preview = embed.preview() || {};

            if (preview.duration) {
                duration = formatDuration(preview.duration);
            }

            if (videoId || duration) {
                return [
                    m('.rich-embed-image-background', preview.images?.length ? {
                        style: {
                            backgroundImage: 'url(' + imageUrl(preview.images[0].src) + ')',
                        },
                    } : {
                        className: 'rich-embed-image-background-missing',
                    }),
                    m('.rich-embed-image-overlay', [
                        videoId ? m('a.rich-embed-play-button', {
                            onclick: (event: Event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                this.youtubePlayer = true;
                            },
                            title: app.translator.trans('kilowhat-rich-embeds.lib.controls.play'),
                        }, icon('fas fa-play')) : null,
                        duration ? m('.rich-embed-duration', duration) : null,
                    ]),
                ];
            }

            return null;
        }

        return m('.rich-embed-embedded-player', m('iframe', {
            src: 'https://www.youtube' + (app.forum.attribute('richEmbedsYoutubeNoCookie') ? '-nocookie' : '') + '.com/embed/' + videoId + (timestamp ? '?start=' + timestamp : ''),
            frameborder: 0,
            allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
            allowfullscreen: 'true',
        }));
    }

    stats(embed: Embed) {
        const preview = embed.preview() || {};
        const stats = preview.stats || [];

        if (!stats.length) {
            return null;
        }

        return m('.rich-embed-stats', stats.map(stat => {
            let value = stat.value;

            if (stat.date) {
                value = humanTime(stat.value as any);
            }

            if (stat.bytes) {
                value = formatSize(stat.value as any);
            }

            if (stat.number) {
                value = formatSize(stat.value as any, '', 'B');
            }

            return m(stat.url ? 'a' : 'span', {
                className: 'rich-embed-stat',
                title: stat.label,
                href: stat.url || undefined,
            }, [
                icon(stat.icon, {
                    className: 'rich-embed-stat-icon',
                }),
                m('span.rich-embed-stat-value', value),
            ]);
        }));
    }
}
